import { lazy, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { NVButton } from '@nv/basic-components'
import { useNVModal } from '@nv/components'
import { useAdminUserContext } from '@nv/contexts'
import { AdminVoterService } from '@nv/services'
import { useTranslation } from 'react-i18next'
import styles from './AdminReports.module.scss'
const ReportsDlDateSelect = lazy(() => import('@nv/lazy-loadable/lib/modals/ReportsDlDateSelect'))
const Downloading = lazy(() => import('@nv/lazy-loadable/lib/modals/Downloading'))

export const AdminReports = () => {
  const { t } = useTranslation()
  const [userType, setUserType] = useState(null)
  const { NVModal: ReportsDlDateSelectModal, openModal: openReportsDlDateSelectModal } = useNVModal()
  const { NVModal: DownloadingModal, openModal: openDownloadingModal, closeModal: closeDownloadingModal } = useNVModal()
  const { adminUser } = useAdminUserContext()

  const canViewPerExtracts =
    KeycloakAdminService.isSuperAdmin() &&
    ['laura@neuvote.com', 'austin.still@calgary.ca', 'quentin.shaw2@calgary.ca', 'ofonime.udokang@calgary.ca'].includes(
      adminUser?.email?.toLowerCase(),
    )

  const downloadReport = async (fn, filename, ext) => {
    openDownloadingModal()
    const res = await fn()
    if (res?.success) {
      const href = URL.createObjectURL(res.data)
      // setDownloadURL(href)
      const link = document.createElement('a')
      link.download = `${filename}-${new Date()}.${ext || 'csv'}` // File name for download
      link.href = href
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      alert('An error occurred')
    }
    closeDownloadingModal()
  }
  const downloadStats = async () => {
    await downloadReport(AdminVoterService.getVoteByMailStats, 'vbm-stats', 'csv')
  }

  const downloadDailyStats = async () => {
    await downloadReport(AdminVoterService.getVoteByMailDailyStats, 'vbm-stats', 'csv')
  }

  const getMailInAdvanceVote = async () => {
    await downloadReport(AdminVoterService.getAdvanceVoteMailInExtract, 'mail-in-extract-adv-voter', 'pdf')
  }

  const getMailInElectionDay = async () => {
    await downloadReport(AdminVoterService.getElectionDayMailInExtract, 'mail-in-extract-election-day', 'zip')
  }

  const getMailInCareFacility = async () => {
    await downloadReport(AdminVoterService.getCareFacilityMailInExtract, 'mail-in-extract-care-facilities', 'zip')
  }

  const getPerExtractElectionDay = async () => {
    await downloadReport(AdminVoterService.getElectionDayPerExtract, 'per-extract-election-day', 'zip')
  }

  const getPerExtractCareFacility = async () => {
    await downloadReport(AdminVoterService.getCareFacilityPerExtract, 'per-extract-care-facilities', 'zip')
  }

  const downloadStrikeOffByDay = async () => {
    await downloadReport(AdminVoterService.getStrikeOffByDay, 'strike-off', 'csv')
  }

  const downloadStrikeOffByDayByWard = async () => {
    await downloadReport(AdminVoterService.getStrikeOffByDayByWard, 'strike-off-by-ward', 'csv')
  }
  const downloadStrikeOffByDayByPollingStation = async () => {
    await downloadReport(AdminVoterService.getStrikeOffByDayByPollingStation, 'strike-off-by-polling-station', 'csv')
  }

  const downloadVbmBallotStats = async () => {
    await downloadReport(AdminVoterService.getVbmBallotStats, 'vbm-ballot-stats', 'csv')
  }

  return (
    <>
      <DownloadingModal>
        <Downloading />
      </DownloadingModal>

      <ReportsDlDateSelectModal>
        <ReportsDlDateSelect userType={userType} />
      </ReportsDlDateSelectModal>

      <div className={styles['container']}>
        <h1 className={styles['header']}>{t('pages.reports')}</h1>

        <div className={styles['reports']}>
          {KeycloakAdminService.isSuperAdmin() && (
            <>
              <div className={styles['reports__card']}>
                <span className={styles['reports__text']}>{t('admin.reports.electorslogReport')}</span>
                <NVButton
                  text={t('buttons.generate')}
                  height={37}
                  onClick={() => {
                    setUserType('voter')
                    openReportsDlDateSelectModal()
                  }}
                />
              </div>
              <div className={styles['reports__seperator']}></div>
              <div className={styles['reports__card']}>
                <span className={styles['reports__text']}>{t('admin.reports.adminLogReport')}</span>
                <NVButton
                  text={t('buttons.generate')}
                  height={37}
                  onClick={() => {
                    setUserType('admin')
                    openReportsDlDateSelectModal()
                  }}
                />
              </div>
            </>
          )}
          <div className={styles['reports__seperator']}></div>
          <div className={styles['reports__card']}>
            <span className={styles['reports__text']}>{t('ELECTION DAY: VBM Ballot Type & Ward Stats')}</span>
            <NVButton
              text={t('buttons.generate')}
              height={37}
              onClick={() => {
                downloadVbmBallotStats()
              }}
            />
          </div>
          <div className={styles['reports__seperator']}></div>
          <div className={styles['reports__card']}>
            <span className={styles['reports__text']}>{t('VBM Latest stats')}</span>
            <NVButton
              text={t('buttons.generate')}
              height={37}
              onClick={() => {
                downloadStats()
              }}
            />
          </div>

          <div className={styles['reports__seperator']}></div>
          <div className={styles['reports__card']}>
            <span className={styles['reports__text']}>{t('VBM Stats By Day')}</span>
            <NVButton
              text={t('buttons.generate')}
              height={37}
              onClick={() => {
                downloadDailyStats()
              }}
            />
          </div>
          <div className={styles['reports__seperator']}></div>
          <div className={styles['reports__card']}>
            <span className={styles['reports__text']}>{t('Live Strike-Off by Day')}</span>
            <NVButton
              text={t('buttons.generate')}
              height={37}
              onClick={() => {
                downloadStrikeOffByDay()
              }}
            />
          </div>
          <div className={styles['reports__seperator']}></div>
          <div className={styles['reports__card']}>
            <span className={styles['reports__text']}>{t('Live Strike-Off by Day & By Ward')}</span>
            <NVButton
              text={t('buttons.generate')}
              height={37}
              onClick={() => {
                downloadStrikeOffByDayByWard()
              }}
            />
          </div>
          <div className={styles['reports__seperator']}></div>
          <div className={styles['reports__card']}>
            <span className={styles['reports__text']}>{t('Live Strike-Off by Day & By Polling Station')}</span>
            <NVButton
              text={t('buttons.generate')}
              height={37}
              onClick={() => {
                downloadStrikeOffByDayByPollingStation()
              }}
            />
          </div>
          {canViewPerExtracts && (
            <>
              <div className={styles['reports__seperator']}></div>
              <div className={styles['reports__card']}>
                <span className={styles['reports__text']}>{t('Mail-in Extract - Adv Vote')}</span>
                <NVButton
                  text={t('buttons.generate')}
                  height={37}
                  onClick={() => {
                    getMailInAdvanceVote()
                  }}
                />
              </div>
              <div className={styles['reports__seperator']}></div>
              <div className={styles['reports__card']}>
                <span className={styles['reports__text']}>{t('Mail-in Extract - Care Facilities')}</span>
                <NVButton
                  text={t('buttons.generate')}
                  height={37}
                  onClick={() => {
                    getMailInCareFacility()
                  }}
                />
              </div>

              <div className={styles['reports__seperator']}></div>
              <div className={styles['reports__card']}>
                <span className={styles['reports__text']}>{t('Mail-in Extract - Election Day')}</span>
                <NVButton
                  text={t('buttons.generate')}
                  height={37}
                  onClick={() => {
                    getMailInElectionDay()
                  }}
                />
              </div>
              <div className={styles['reports__seperator']}></div>
              <div className={styles['reports__card']}>
                <span className={styles['reports__text']}>{t('Care Facility Extracts')}</span>
                <NVButton
                  text={t('buttons.generate')}
                  height={37}
                  onClick={() => {
                    getPerExtractCareFacility()
                  }}
                />
              </div>
              <div className={styles['reports__seperator']}></div>
              <div className={styles['reports__card']}>
                <span className={styles['reports__text']}>{t('Election Day VSD Extracts')}</span>
                <NVButton
                  text={t('buttons.generate')}
                  height={37}
                  onClick={() => {
                    getPerExtractElectionDay()
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
